import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Loader2, Flame, History, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { neon, useLaunch, Move, Icon, ago } from "./shared";

const P = [["day", "Daily"], ["week", "Weekly"], ["month", "Monthly"]];
const medal = (r) => (r === 1 ? "bg-amber-400 text-black" : r === 2 ? "bg-slate-300 text-black" : r === 3 ? "bg-orange-500 text-black" : "bg-[#312e81] text-white");

export function TopApps() {
  const launch = useLaunch();
  const [period, setPeriod] = useState("day");
  const [d, setD] = useState(null);
  const [all, setAll] = useState(false);
  useEffect(() => {
    let live = true;
    const load = () => api.get("/home/rankings", { params: { period, limit: 100 } }).then((r) => live && setD(r.data)).catch(() => live && setD({ rows: [] }));
    setD(null); load();
    const t = setInterval(load, 60000);
    return () => { live = false; clearInterval(t); };
  }, [period]);
  const rows = d?.rows || [];
  return (
    <section id="top-apps" className={`${neon} p-4 md:p-6`} data-testid="home-top-apps">
      <div className="flex flex-wrap items-center gap-3">
        <Trophy className="w-8 h-8 text-amber-300" />
        <div className="flex-1 min-w-0"><h2 className="text-xl md:text-2xl font-black">Top 100 Apps</h2><p className="text-xs text-white/60">Real member activity. Rankings update as usage changes.</p></div>
        <div className="flex rounded-xl overflow-hidden border border-cyan-400/40">{P.map(([k, l]) => <button key={k} onClick={() => setPeriod(k)} className={`px-3 py-1.5 text-xs font-bold ${period === k ? "bg-cyan-400 text-black" : "hover:bg-white/5"}`} data-testid={`top-apps-${k}`}>{l}</button>)}</div>
      </div>
      {!d ? <div className="py-12 grid place-items-center"><Loader2 className="animate-spin text-cyan-300" /></div>
        : !rows.length ? <p className="py-10 text-center text-sm text-white/60" data-testid="top-apps-empty">No app launches in this period yet — rankings appear as members use apps.</p> : <>
          <div className="hidden md:grid grid-cols-[3rem_1fr_9rem_4rem_4rem] gap-3 text-[11px] uppercase tracking-wider text-white/50 px-2 mt-5 mb-1"><span>#</span><span>App</span><span>Popularity</span><span>Previous</span><span>Change</span></div>
          <div className="space-y-1.5 mt-3 md:mt-0" data-testid="top-apps-rows">
            {(all ? rows : rows.slice(0, 10)).map((r) => (
              <div key={r.app.slug} className="grid grid-cols-[2.5rem_1fr_3.5rem] md:grid-cols-[3rem_1fr_9rem_4rem_4rem] gap-3 items-center rounded-2xl px-2 py-2 hover:bg-white/5" data-testid={`top-app-row-${r.rank}`}>
                <span className={`w-9 h-9 rounded-full grid place-items-center font-black ${medal(r.rank)}`}>{r.rank}</span>
                <button onClick={() => launch(r.app)} className="flex items-center gap-3 min-w-0 text-left" data-testid={`top-app-launch-${r.app.slug}`}>
                  <Icon app={r.app} size="w-11 h-11" />
                  <span className="min-w-0"><span className="block font-bold truncate">{r.app.name}</span>
                    <span className="block text-[11px] text-white/55 truncate">{r.members} member{r.members === 1 ? "" : "s"} · D #{r.ranks.day ?? "–"} · W #{r.ranks.week ?? "–"} · M #{r.ranks.month ?? "–"}</span></span>
                </button>
                <span className="hidden md:flex items-center gap-2"><span className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden"><span className="block h-full bg-gradient-to-r from-cyan-400 to-fuchsia-500" style={{ width: `${r.popularity}%` }} /></span><span className="text-xs w-9">{r.popularity}%</span></span>
                <span className="hidden md:block text-sm text-white/70">{r.prev ? `#${r.prev}` : "—"}</span>
                <span className="text-right md:text-left"><Move move={r.move} testid={`top-app-move-${r.rank}`} /></span>
              </div>
            ))}
          </div>
          {rows.length > 10 && <button onClick={() => setAll(!all)} className="mt-3 w-full text-sm font-bold text-cyan-300 hover:text-cyan-200" data-testid="top-apps-toggle">{all ? "Show top 10" : `Show all ${rows.length}`}</button>}
          <p className="text-[10px] text-white/45 mt-3" data-testid="top-apps-rule">{d.rule} Change compares with the previous {period === "day" ? "day" : period}.</p>
        </>}
    </section>
  );
}

function Panel({ icon: I, title, sub, children, testid }) {
  return <section className={`${neon} p-4 md:p-5`} data-testid={testid}><div className="flex items-center gap-2 mb-3"><I className="w-6 h-6 text-cyan-300" /><div><h3 className="font-black">{title}</h3><p className="text-[11px] text-white/55">{sub}</p></div></div>{children}</section>;
}

export function HomeSections({ data }) {
  const launch = useLaunch();
  if (!data) return null;
  const row = (a, right, key, tid) => (
    <div key={key} className="flex items-center gap-3 py-1.5" data-testid={tid}>
      <Icon app={a} size="w-10 h-10" />
      <Link to={`/app/${a.slug}`} className="flex-1 min-w-0"><p className="text-sm font-bold truncate">{a.name}</p><p className="text-[11px] text-white/55 capitalize truncate">{a.category}</p></Link>
      {right}
    </div>
  );
  return (
    <div className="grid md:grid-cols-3 gap-4">
      <Panel icon={Flame} title="Trending Today" sub="Biggest movers in the last 24 hours" testid="home-trending-today">
        {!data.trending.length ? <p className="text-xs text-white/55">No activity yet today.</p> : data.trending.slice(0, 5).map((t) => row(t.app, <Move move={t.move} />, t.app.slug, `trending-${t.app.slug}`))}
      </Panel>
      <Panel icon={History} title="Continue Where You Left Off" sub="Jump back into your apps and games" testid="home-continue">
        {!data.continue.length ? <p className="text-xs text-white/55">{"Apps you open will show up here."}</p> : data.continue.slice(0, 5).map((c) => row(c.app, <><span className="text-[10px] text-white/50 hidden sm:block">{ago(c.last_at)}</span><button onClick={() => launch(c.app)} className="px-3 py-1.5 rounded-lg text-xs font-black bg-gradient-to-r from-cyan-400 to-fuchsia-500" data-testid={`continue-launch-${c.app.slug}`}>Launch</button></>, c.app.slug, `continue-${c.app.slug}`))}
      </Panel>
      <Panel icon={Sparkles} title="New on Jixxeo" sub="Fresh apps, new features, more fun" testid="home-new">
        {data.new.slice(0, 5).map((a) => row(a, <button onClick={() => launch(a)} className="px-3 py-1.5 rounded-lg text-xs font-black border border-cyan-400/50 hover:bg-cyan-400/10" data-testid={`new-launch-${a.slug}`}>Launch</button>, a.slug, `new-${a.slug}`))}
      </Panel>
    </div>
  );
}
